import { FC, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Wrapper,
  EllipsisSpan,
  useUpdate,
  useBreadcrumbRoutes,
  Button,
  useAsync,
  DatePicker,
  Form,
  Tag,
  Modal,
  Input,
  Select,
  Tabs,
  CustomFilter,
  ShowInput,
  Dropdown,
  FieldTable,
  Paging,
  usePaging,
  getBase,
  MultiSelect,
} from '@maxtropy/components';
import { Cascader, InputNumber, Space } from 'antd';
import {
  AlarmLevel,
  AlarmLevelColorDisplay,
  AlarmLevelDisplay,
  AlarmState,
  AlarmStateColorDisplay,
  AlarmStateDisplay,
} from '@/shared/types';
import { Link, useLocation } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import {
  AlarmLogRequest,
  AlarmLogResponse,
  deviceAssetsResponse,
  deviceAssetTypeEmun,
  getAlarmLogList,
  readAlarmLog,
} from '../../../api/alarm';
import { useHasPermission } from '../../../utils/utils';
import { PermissionsType } from '../../../common/permissionsConst';
import { qingflowWorkDetailPath, TabKeyEnum, TabKeyStateDisplay } from '../const';
import { getOuListOwned } from '../../../api/ou';
import { batchReadAlarmLog } from '../../../api/rule';
import qs from 'qs';
import {
  apiV2ServerAlarmRecordRecoveryPost,
  apiV2RuleAlarmLogAlarmLevelCountPost,
  V2RuleAlarmLogAlarmLevelCountPostResponse,
  apiV2ComprehensiveLabelListPost,
  apiV2RuleAlarmLogAlarmRuleListPost,
  apiV2RuleAlarmLogAlarmLogDeletePost,
} from '@maxtropy/device-customer-apis-v2';
import { DefaultOptionType } from 'antd/es/cascader';
import { formatOptionData, getChildNodesByParentIds } from '@/shared/components/CascadingMultipleSelector/utils';
import { getDeviceTypeTree } from '@/api/deviceType';
import { getPhysicalModelList, PhysicalModelRes } from '@/api/device';
import { useWatch } from 'antd/es/form/Form';
import GradeSwitch from './GradeSwitch';
import AssociateLoopsModal from './AssociateLoopsModal';
import styles from './index.module.scss';
import { apiV2RuleRuleListPost, V2RuleRuleListPostResponse } from '@maxtropy/device-customer-apis-v2';
const { RangePicker } = DatePicker;

const dateFormat = 'YYYY/MM/DD HH:mm';
const displayDateFormat = 'YYYY/MM/DD HH:mm:ss';

interface FilterParams {
  tenantMcid?: string;
  ouIds?: number[];
  deviceCode?: string;
  deviceName?: string;
  state?: number;
  time?: [Dayjs, Dayjs];
  deviceAssetCode?: string;
  deviceTypeId?: number[][];
  physicalModelId?: number[];
  alarmName?: string;
  ruleInfo?: string;
  ruleIds?: number[];
  durationFilter?: string; // 'gte' | 'lte' | 'eq' | 'gt' | 'lt'
  duration?: number;
  durationUnit?: string; // 'minutes' | 'hours' | 'days'
  alarmRuleTags?: number[];
  deviceTags?: number[];
}

type SearchParams = Omit<AlarmLogRequest, 'page' | 'size'> & {
  durationFilter?: string;
  duration?: number;
  durationUnit?: string;
  deviceLabelIds?: number[];
  ruleLabelIds?: number[];
};

const AlarmRecordList: FC = () => {
  const [form] = Form.useForm();
  const [updateState, updateFn] = useUpdate();
  const location = useLocation();
  const [isSortValue, setIsSortValue] = useState<boolean>(false); // 是否传sortvalue
  const [searchParams, setSearchParams] = useState<SearchParams>();

  // 自动刷新状态
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  // 定时器引用
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  const ou = useAsync(getOuListOwned, []);
  const breadcrumbRoutes = useBreadcrumbRoutes();

  const hasCreateWorkOrderPers = useHasPermission(PermissionsType.B_WORKER_ORDER_CREATE);
  const hasSubmitOrderPers = useHasPermission(PermissionsType.B_SUBMIT_WORKER_ORDER);
  const hasDisplayedFieldsList = useHasPermission(PermissionsType.B_SETDISPLAYEDFIELDSLIST);
  const hasViewEntityDetails = useHasPermission(PermissionsType.B_VIEWENTITYDETAILS);
  const hasCopyRuleNumber = useHasPermission(PermissionsType.B_COPYRULENUMBER);
  const hasConfirmReadButton = useHasPermission(PermissionsType.B_CONFIRMREADBUTTON);

  const [energyAssetInfo, setEnergyAssetInfo] = useState<deviceAssetsResponse[]>([]);
  const [showEnergyAssetInfoVisible, setShowEnergyAssetInfoVisible] = useState<boolean>(false);

  //先定义两个空的数组，用于存放选中的id和数据
  const [checkedData, setCheckedData] = useState<any>([]); // 选中的数据
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);

  // 设备类目
  const deviceTypeData = useAsync(getDeviceTypeTree);
  const [physicalModelList, setPhysicalModelList] = useState<PhysicalModelRes[]>([]); // all 物模型型号列表

  const [ruleListOp, setRuleListOp] = useState<V2RuleRuleListPostResponse['list']>([]); // all 规则列表
  const [alarmRuleTagOptions, setAlarmRuleTagOptions] = useState<Array<{ label: string; value: number }>>([]); // 报警规则标签选项
  const [deviceTagOptions, setDeviceTagOptions] = useState<Array<{ label: string; value: number }>>([]); // 设备标签选项

  const deviceTypeId = useWatch('deviceTypeId', form);

  const [gradeDatas, setGradeDatas] = useState<V2RuleAlarmLogAlarmLevelCountPostResponse>();
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const [displayAssociate, setDisplayAssociate] = useState<string>();
  const [tabKey, setTabKey] = useState(TabKeyEnum.UNRESTORED_ALARM);
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  useEffect(() => {
    // 物模型型号
    if (deviceTypeId) {
      const typeIds = getChildNodesByParentIds(deviceTypeId, deviceTypeData?.tree);
      getPhysicalModelList({ deviceTypeIds: typeIds }).then(res => setPhysicalModelList(res.list));
    } else {
      getPhysicalModelList({}).then(res => setPhysicalModelList(res.list));
    }
  }, [deviceTypeId]);

  useEffect(() => {
    apiV2ComprehensiveLabelListPost({ id: 2 }).then(res => {
      setAlarmRuleTagOptions(res.list?.map(r => ({ label: r.name || '', value: r.id || 0 })) || []);
    });

    apiV2ComprehensiveLabelListPost({ id: 3 }).then(res => {
      setDeviceTagOptions(res.list?.map(r => ({ label: r.name || '', value: r.id || 0 })) || []);
    });
  }, []);
  const reLoadRuleList = () => {
    const value = form.getFieldsValue(true) as FilterParams;
    const currentTime = dayjs();
    const endtime = value.time ? dayjs(value.time[1]) : undefined;
    const typeId = getChildNodesByParentIds(value.deviceTypeId, deviceTypeData?.tree);
    // 1. 整合所有参数（包含原有的固定参数和 formValue 关联参数）
    const requestParams = {
      page: 1,
      size: 100,
      state: Number(tabKey),
      deviceTypeId: typeId,
      physicalModelId: value.physicalModelId,
      ouIds: value.ouIds,
      deviceCode: value.deviceCode,
      deviceName: value.deviceName,
      startTime: value.time ? dayjs(value.time[0]).valueOf() : undefined,
      endTime: endtime ? (endtime.isAfter(currentTime) ? currentTime : endtime).valueOf() : undefined,
      deviceAssetCode: value.deviceAssetCode,
      alarmName: value.alarmName,
      durationFilter: value.durationFilter,
      duration: value.duration,
      durationUnit: value.durationUnit,
      deviceLabelIds: value.deviceTags,
      ruleLabelIds: value.alarmRuleTags,
      // 若需要保留 searchParams（排除 ruleIds 后），取消下面注释
      // ...(searchParams && (({ ruleIds, ...rest }) => rest)(searchParams)),
    };

    // 2. 过滤无效值：剔除 undefined、null、空字符串（''）
    const validParams = Object.fromEntries(
      Object.entries(requestParams).filter(([_, val]) => {
        // 条件：非 undefined + 非 null + 非空字符串
        return val !== undefined && val !== null && val !== '';
      })
    );

    // 3. 发起请求（仅传递有效参数）
    apiV2RuleAlarmLogAlarmRuleListPost(validParams).then(res => {
      setRuleListOp(res.list);
    });
  };

  const columns = [
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      ellipsis: { showTitle: true },
      width: 300,
      fixed: true,
      render: (v: string, record: AlarmLogResponse) => {
        return (
          <Link to={`/device/manage/device/${record?.deviceId}/detail`} target="_blank">
            <EllipsisSpan value={v} />
          </Link>
        );
      },
    },
    {
      title: '报警等级',
      dataIndex: 'alarmLevel',
      width: 100,
      ellipsis: { showTitle: true },
      fixed: true,
      render: (v: AlarmLevel) => (
        <ShowInput
          value={
            <Tag border="solid" color={AlarmLevelColorDisplay[v]}>
              {AlarmLevelDisplay[v]}
            </Tag>
          }
        />
      ),
    },
    {
      title: '报警状态',
      dataIndex: 'state',
      width: 100,
      ellipsis: { showTitle: true },
      fixed: true,
      render: (v: AlarmState) => (
        <ShowInput
          value={
            <Tag border="solid" color={AlarmStateColorDisplay[v]}>
              {AlarmStateDisplay[v]}
            </Tag>
          }
        />
      ),
    },
    {
      title: '规则名称',
      dataIndex: 'ruleName',
      width: 300,
      fixed: true,
      render: (v: string) => <EllipsisSpan value={v} />,
    },

    {
      title: '报警信息',
      dataIndex: 'alarmName',
      width: 120,
      fixed: true,
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '报警时间',
      width: 180,
      dataIndex: 'alarmTime',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format(displayDateFormat) : undefined} />,
    },
    {
      title: '恢复时间',
      width: 180,
      dataIndex: 'recoveryTime',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format(displayDateFormat) : '未恢复'} />,
    },
    {
      title: '持续时间',
      width: 180,
      dataIndex: 'duration',
      ellipsis: { showTitle: true },
      render: (v: string, record: any) => {
        //const { alarmTime, recoveryTime, duration } = record;
        // if (!alarmTime || !recoveryTime) return <EllipsisSpan value={'--'} />;

        // const start = dayjs(alarmTime);
        // const end = dayjs(recoveryTime);
        // const diffMs = end.diff(start);

        // // 总分钟数
        // const totalMinutes = Math.floor(diffMs / (1000 * 60));
        // const days = Math.floor(totalMinutes / (60 * 24));
        // const hours = Math.floor((totalMinutes - days * 24 * 60) / 60);
        // const minutes = totalMinutes - days * 24 * 60 - hours * 60;

        // let durationText = '';
        // if (days > 0) durationText += `${days}天`;
        // if (hours > 0) durationText += `${hours}时`;
        // if (minutes > 0) durationText += `${minutes}分`;

        return <EllipsisSpan value={v} />;
      },
    },
    ...[
      !window.alarmRecordReadTimeHide
        ? {
            title: '已读时间',
            width: 180,
            dataIndex: 'readTime',
            ellipsis: { showTitle: true },
            render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format(displayDateFormat) : undefined} />,
          }
        : undefined,
    ],

    {
      title: '规则编号',
      dataIndex: 'alarmCode',
      width: 180,
      ellipsis: { showTitle: true },
      fixed: true,
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '设备编号',
      dataIndex: 'deviceCode',
      width: 180,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '所属类目',
      dataIndex: 'deviceType',
      width: 180,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '物模型',
      dataIndex: 'physicalModel',
      width: 120,
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '能源资产信息',
      dataIndex: 'deviceAssets',
      ellipsis: { showTitle: true },
      width: 130,
      render: (v: string, record: AlarmLogResponse) => {
        return !record.deviceAssets || record.deviceAssets.length === 0 ? (
          '暂无'
        ) : (
          <Button type="link" onClick={() => showEnergyAssetInfo(record.deviceAssets!)}>
            点击查看
          </Button>
        );
      },
    },
    {
      title: '报警规则标签',
      dataIndex: 'ruleLabelNames',
      width: 120,
      ellipsis: { showTitle: true },
      render: (v: string[]) => <EllipsisSpan value={v?.join('、') || ''} />,
    },
    {
      title: '设备标签',
      dataIndex: 'deviceLabelNames',
      width: 120,
      ellipsis: { showTitle: true },
      render: (v: string[]) => <EllipsisSpan value={v?.join('、') || ''} />,
    },
  ];

  useEffect(() => {
    const query = qs.parse(location.search, { ignoreQueryPrefix: true });
    if (query.ouId || query.codeOrName || query.deviceAssetCode || query.startTime || query.endTime) {
      let ouId = query?.ouId ? (isNaN(Number(query.ouId)) ? undefined : Number(query.ouId)) : undefined;
      let ouItemId = ou.find(m => m.id === ouId)?.id;
      let codeOrName = query?.codeOrName ? String(query.codeOrName) : undefined;
      let deviceAssetCode = query?.deviceAssetCode ? String(query.deviceAssetCode) : undefined;
      let state = query?.state ? (isNaN(Number(query.state)) ? undefined : Number(query.state)) : undefined;
      let labelIds = query?.labelIds
        ? String(query.labelIds)
            .split(',')
            .map((id: string) => Number(id))
        : undefined;
      console.log('labelIds :>> ', labelIds);
      form.setFieldsValue({
        deviceTags: labelIds,
        ouIds: ouItemId ? [ouItemId] : undefined,
        deviceCode: codeOrName,
        deviceAssetCode,
        state,
        time:
          query.startTime && query.endTime ? [dayjs(Number(query.startTime)), dayjs(Number(query.endTime))] : undefined,
      });
      setSearchParams({
        ...searchParams,
        startTime: query.startTime ? Number(query.startTime) : undefined,
        endTime: query.endTime ? Number(query.endTime) : undefined,
        deviceLabelIds: labelIds,
        ouIds: ouItemId ? [ouItemId] : undefined,
        deviceCode: codeOrName,
        deviceAssetCode,
        state,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, form, ou]);

  const [records, setRecords] = useState<AlarmLogResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [levels, setLevels] = useState<number[]>([]);
  const [sortValues, setSortValues] = useState<any[]>();

  useEffect(() => {
    setLoading(true);
    getAlarmLogList({
      ...searchParams,
      levels,
      sortValues: isSortValue ? sortValues : undefined,
      page: pageOffset,
      size: pageSize,
      state: Number(tabKey),
    }).then(res => {
      setLoading(false);
      if (res) {
        if (res.useSearchAfter) {
          setSortValues(res.sortValues);
          setIsSortValue(true);
        } else {
          setSortValues(undefined);
          setIsSortValue(false);
        }
        setRecords(res.list);
        setTotalCount(res.total);
      }
    });
    apiV2RuleAlarmLogAlarmLevelCountPost({
      ...searchParams,
      levels: [],
      sortValues: isSortValue ? sortValues : undefined,
      page: pageOffset,
      size: pageSize,
      state: tabKey,
    }).then(res => {
      setGradeDatas(res);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, pageOffset, setTotalCount, searchParams, updateState, tabKey, levels]);
  const onFinish = (value: FilterParams) => {
    setIsSortValue(false);
    setPageOffset(1);
    const endtime = value.time ? dayjs(value.time[1]) : undefined;
    const typeId = getChildNodesByParentIds(value.deviceTypeId, deviceTypeData?.tree);
    const currentTime = dayjs();
    const params: SearchParams = {
      deviceTypeId: typeId,
      physicalModelId: value.physicalModelId,
      ouIds: value.ouIds,
      deviceCode: value.deviceCode,
      deviceName: value.deviceName,
      state: value.state,
      startTime: value.time ? dayjs(value.time[0]).valueOf() : undefined,
      endTime: endtime ? (endtime.isAfter(currentTime) ? currentTime : endtime).valueOf() : undefined,
      deviceAssetCode: value.deviceAssetCode,
      alarmName: value.alarmName,
      ruleIds: value.ruleIds,
      durationFilter: value.durationFilter,
      duration: value.duration,
      durationUnit: value.durationUnit,
      deviceLabelIds: value.deviceTags,
      ruleLabelIds: value.alarmRuleTags,
    };
    setSearchParams(params);
  };

  const onReset = () => {
    const params = {
      ouIds: undefined,
      deviceCode: undefined,
      deviceName: undefined,
      levels: undefined,
      state: undefined,
      time: undefined,
      deviceAssetCode: undefined,
      deviceTypeId: undefined,
      physicalModelId: undefined,
      ruleIds: undefined,
      durationFilter: undefined,
      duration: undefined,
      durationUnit: undefined,
      alarmRuleTags: undefined,
      deviceTags: undefined,
    };
    setSearchParams(params);
    setPageOffset(1);
  };

  const readLog = (id: number) => {
    readAlarmLog(id).then(res => {
      if (res.flag) {
        Modal.success({
          title: '已读！',
          okText: '确定',
          onOk() {
            updateFn();
          },
          onCancel() {
            updateFn();
          },
        });
      }
    });
  };

  const readAllLog = () => {
    Modal.confirm({
      title: '确认批量已读当前页的报警记录？',
      content: (
        <span>
          <span style={{ color: 'var(--error-color)' }}>已读后不可恢复</span>
          ，你还确认要继续吗？
        </span>
      ),
      okText: '确认',
      onOk: () => {
        batchReadAlarmLog(records.map(i => i.id)).then(() => {
          Modal.success({
            content: '已读当前页报警记录操作成功。',
            okText: '确认',
            onOk() {
              updateFn();
            },
            onCancel() {
              updateFn();
            },
          });
        });
      },
    });
  };

  const onCopy = (v: string | undefined) => {
    if (!v) return;
    if (getBase()) {
      window.rawWindow.navigator.clipboard.writeText(v);
    } else {
      navigator.clipboard.writeText(v);
    }
  };

  const buildColumns = [
    ...columns.filter(item => item !== undefined),
    {
      title: '操作',
      dataIndex: 'operation',
      width: 80,
      showForce: true,
      fixed: 'right' as const,
      render: (value: undefined, record: AlarmLogResponse) => {
        return (
          <Space size={16}>
            <Dropdown
              type="link"
              menu={{
                items: [
                  {
                    show: hasConfirmReadButton && !window.alarmRecordReadTimeHide,
                    key: '1',

                    disabled: !!record?.readTime,
                    label: (
                      <span
                        onClick={() => {
                          readLog(record.id);
                        }}
                      >
                        {record?.readTime ? '已读' : '确认已读'}
                      </span>
                    ),
                  },
                  {
                    show: hasSubmitOrderPers,
                    key: '2',
                    label: (
                      <span
                        onClick={() => {
                          window.open(
                            `${window.ALARMWORKORDER}/orderv2/order/add/index?${qs.stringify(
                              {
                                problemSource: 10,
                                operationUnitCode: (record.ous?.map(item => item.id) ?? []).join(','),
                                alarmId: record.id,
                                alarmTypeCode: -1,
                              },
                              { indices: false }
                            )}`,
                            '_blank'
                          );
                        }}
                      >
                        发起工单
                      </span>
                    ),
                  },
                  {
                    show: hasCreateWorkOrderPers && record.ruleLogWorkOrderId,
                    key: '3',
                    label: <span onClick={() => window.open(qingflowWorkDetailPath, '_blank')}>查看工单</span>,
                  },
                  {
                    show: hasCreateWorkOrderPers && !record.ruleLogWorkOrderId,
                    key: '4',
                    label: <Link to={`/device/alarm/record/${record.id}/create`}>创建工单</Link>,
                  },
                  {
                    show: true,
                    key: '5',
                    label: (
                      /* 点击设备数据查询关联属性 */
                      <Link
                        to={`/data/history/device?deviceId=${record.deviceId}&alarmTime=${dayjs(
                          record.alarmTime
                        ).valueOf()}`}
                        target="_blank"
                      >
                        查看设备数据
                      </Link>
                    ),
                  },
                  {
                    show: true,
                    key: '6',
                    label: (
                      <span
                        onClick={() =>
                          window.open(`/device/alarm/record/detail/${record.id}?state=${record?.state}`, '_blank')
                        }
                      >
                        查看报警详情
                      </span>
                    ),
                  },
                  {
                    show: hasCopyRuleNumber,
                    key: '7',
                    label: <span onClick={() => onCopy(record.alarmCode)}>复制规则编号</span>,
                  },
                  {
                    show: hasViewEntityDetails,
                    key: '8',
                    label: (
                      <span
                        onClick={() => {
                          setIsVisibleModal(true);
                          setDisplayAssociate(record.deviceId);
                        }}
                      >
                        查看关联实体
                      </span>
                    ),
                  },
                ].filter(item => item.show),
              }}
            >
              操作项
            </Dropdown>
          </Space>
        );
      },
    },
  ];
  const showEnergyAssetInfo = (val: deviceAssetsResponse[]) => {
    setShowEnergyAssetInfoVisible(true);
    setEnergyAssetInfo(val);
  };

  const ouOptions = useMemo(() => {
    if (ou?.length !== 0) {
      return ou?.map(item => ({ label: item.name, value: item.id }));
    }
  }, [ou]);

  const tableSelect = (record: any, selected: any) => {
    //如果选中某一行，把选中的这行数据及id分别存到checkedData、selectedKeys中
    if (selected) {
      const arr = checkedData.map((item: any) => item.id);
      setSelectedKeys([...arr, record.id]);
      setCheckedData([...checkedData, record]);
      //取消选中，则在checkedData、selectedKeys中过滤掉这条数据
    } else {
      const newData = checkedData.filter((item: any) => item.id !== record.id);
      setCheckedData(newData);
      const arr = newData.map((item: any) => {
        return item.id;
      });
      setSelectedKeys([...arr]);
    }
  };
  // 表格全选/取消全选
  const tableSelectAll = (selected: boolean, selectedRows: any, changeRows: any) => {
    if (!selected) {
      setSelectedKeys([]);
      setCheckedData([]);
    }
    //全选，把
    else {
      const arr = changeRows.map((item: any) => item.id);
      setCheckedData([...checkedData, ...changeRows]);
      setSelectedKeys([...selectedKeys, ...arr]);
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedKeys, //展示选中的数据
    onSelect: tableSelect, //单条数据取消/选中的回调
    onSelectAll: tableSelectAll, //全选/取消全选的回调
  };

  const adjustRestore = () => {
    Modal.confirm({
      title: `确定解除报警？`,
      onOk: () => {
        apiV2ServerAlarmRecordRecoveryPost({ alarmIds: selectedKeys }).then(_ => {
          setSelectedKeys([]);
          setCheckedData([]);
          updateFn();
        });
      },
    });
  };
  const batchDel = () => {
    Modal.confirm({
      title: `是否批量删除选中的报警记录，删除后无法恢复；`,
      onOk: () => {
        apiV2RuleAlarmLogAlarmLogDeletePost({ ids: selectedKeys }).then(_ => {
          setSelectedKeys([]);
          setCheckedData([]);
          updateFn();
        });
      },
    });
  };

  const exportAction = () => {
    window.open(
      `/api/v2/rule/alarm/log/exportAlarm` +
        qs.stringify({ ...searchParams, state: tabKey, levels }, { addQueryPrefix: true })
    );
  };
  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

  const objectModalTypeOptions = useMemo(() => {
    if (physicalModelList && physicalModelList.length > 0) {
      return physicalModelList.map(i => ({ label: `${i.modelNo}-${i.manufacturerName}`, value: i.id }));
    }
    return [];
  }, [physicalModelList]);

  const items = [
    {
      key: TabKeyEnum.UNRESTORED_ALARM,
      label: TabKeyStateDisplay[TabKeyEnum.UNRESTORED_ALARM],
    },
    {
      key: TabKeyEnum.RESTORED_ALARM,
      label: TabKeyStateDisplay[TabKeyEnum.RESTORED_ALARM],
    },
  ];

  // 手动刷新列表（带原有参数）
  const manualRefreshList = useCallback(() => {
    setLoading(true);
    getAlarmLogList({
      ...searchParams,
      levels,
      sortValues: isSortValue ? sortValues : undefined,
      page: pageOffset,
      size: pageSize,
      state: Number(tabKey),
    })
      .then(res => {
        setLoading(false);
        if (res) {
          if (res.useSearchAfter) {
            setSortValues(res.sortValues);
            setIsSortValue(true);
          } else {
            setSortValues(undefined);
            setIsSortValue(false);
          }
          setRecords(res.list);
          setTotalCount(res.total);
        }
      })
      .catch(() => setLoading(false));
    apiV2RuleAlarmLogAlarmLevelCountPost({
      ...searchParams,
      levels: [],
      sortValues: isSortValue ? sortValues : undefined,
      page: pageOffset,
      size: pageSize,
      state: tabKey,
    }).then(res => {
      setGradeDatas(res);
    });
  }, [searchParams, levels, isSortValue, sortValues, pageOffset, pageSize, tabKey, setTotalCount]);

  // 重置定时器函数
  const resetAutoRefreshTimer = useCallback(() => {
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
    if (autoRefresh) {
      autoRefreshTimerRef.current = setInterval(() => {
        manualRefreshList();
      }, 180000);
    }
  }, [autoRefresh, manualRefreshList]);

  // 自动刷新逻辑：勾选后每10秒刷新，用户操作重新计时
  useEffect(() => {
    resetAutoRefreshTimer();

    // 监听用户活动事件，重新计时
    const handleUserActivity = () => {
      if (autoRefresh) {
        resetAutoRefreshTimer();
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'input', 'change', 'focus'];
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });

    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [autoRefresh, resetAutoRefreshTimer]);

  const filters = (
    <div>
      <Tabs defaultActiveKey="1" items={items} selectedLinePlacement="top" onChange={setTabKey} />
      <div style={{ padding: '0 20px' }}>
        <CustomFilter
          form={form}
          onFinish={val => {
            onFinish(val as FilterParams);
          }}
          onReset={onReset}
          collapseOpen={true}
        >
          <Form.Item name="ruleIds" label="规则编码/名称">
            <MultiSelect
              onDropdownVisibleChange={open => {
                if (open) {
                  // 可选：只在未加载规则列表时请求，避免重复请求
                  //setSearchParams(prev => (prev ? { ...prev } : ({} as SearchParams)));
                  reLoadRuleList();
                }
              }}
              style={{ width: '100%' }}
              placeholder="请输入规则编码或者名称查询"
              optionFilterProp="label"
              maxTagCount={3}
              showSearch
              allowClear
              options={ruleListOp?.map(r => ({
                label: `${r.name}${r.num !== undefined ? `\u00A0\u00A0\u00A0(${r.num})` : ''}`, // 3个 \u00A0 代表3个不折叠的空格
                value: r.id,
              }))}
            />
          </Form.Item>

          <Form.Item name="deviceName" label="设备名称">
            <Input placeholder="请输入设备名称查询" />
          </Form.Item>

          <Form.Item name="time" label="报警日期">
            <RangePicker
              style={{ width: '100%' }}
              showTime={{
                format: 'HH:mm',
              }}
              // disabledDate={date => {
              //   return dayjs().isBefore(date);
              // }}
              format={dateFormat}
            />
          </Form.Item>

          <Form.Item name="alarmName" label="报警信息" rules={[{ max: 30, message: '请输入报警信息查询' }]}>
            <Input placeholder="请输入报警信息查询" />
          </Form.Item>

          <Form.Item name="ouIds" label="运营单元">
            <Select
              style={{ width: '100%' }}
              placeholder="请选择"
              options={ouOptions}
              showSearch
              optionFilterProp="label"
              mode="multiple"
              maxTagCount={3}
            />
          </Form.Item>

          <Form.Item
            name="deviceAssetCode"
            label="能源资产编号"
            rules={[{ max: 30, message: '能源资产编号最多30个字符' }]}
          >
            <Input placeholder=" 请输入能源资产编号" />
          </Form.Item>

          <Form.Item name="deviceCode" label="设备编号">
            <Input placeholder="请输入设备编号查询" />
          </Form.Item>

          <Form.Item name="deviceTypeId" label="所属类目">
            <Cascader
              onChange={() => form.setFieldValue('physicalModelId', undefined)}
              options={formatOptionData(deviceTypeData)}
              allowClear={false}
              fieldNames={{ children: 'child' }}
              multiple
              maxTagCount="responsive"
              showSearch={{ filter }}
              placeholder={'请选择所属类目'}
            />
          </Form.Item>

          <Form.Item name="physicalModelId" label="物模型型号">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              showSearch
              options={objectModalTypeOptions}
              placeholder={'请选择'}
            />
          </Form.Item>

          {/* add 报警持续时间筛选 */}
          <Form.Item label="报警持续时间">
            <div style={{ display: 'flex' }}>
              <Form.Item name="durationFilter" noStyle>
                <Select placeholder="条件" allowClear style={{ width: 80 }}>
                  <Select.Option value="4">{'>='}</Select.Option>
                  <Select.Option value="5">{'<='}</Select.Option>
                  <Select.Option value="3">=</Select.Option>
                  <Select.Option value="1">{'>'}</Select.Option>
                  <Select.Option value="2">{'<'}</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="duration" noStyle>
                <InputNumber placeholder="时长" min={0} max={9999.99} precision={2} style={{ flex: 1 }} />
              </Form.Item>
              <Form.Item name="durationUnit" noStyle>
                <Select placeholder="单位" style={{ width: 80 }} allowClear>
                  <Select.Option value="1">分钟</Select.Option>
                  <Select.Option value="2">小时</Select.Option>
                  <Select.Option value="3">天</Select.Option>
                </Select>
              </Form.Item>
            </div>
          </Form.Item>

          <Form.Item name="alarmRuleTags" label="报警规则标签">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              showSearch
              maxTagCount={3}
              options={alarmRuleTagOptions}
              placeholder={'请选择报警规则标签'}
            />
          </Form.Item>

          <Form.Item name="deviceTags" label="设备标签">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              showSearch
              maxTagCount={3}
              options={deviceTagOptions}
              placeholder={'请选择设备标签'}
            />
          </Form.Item>
        </CustomFilter>
      </div>
    </div>
  );

  return (
    <Wrapper
      routes={breadcrumbRoutes?.routes}
      filters={filters}
      className={styles.wrapper}
      filtersStyle={{ padding: 0 }}
      hasPadding={false}
    >
      <div style={{ background: 'var(--mx-module-bg-color)', flex: 1, padding: '20px' }}>
        <GradeSwitch
          selectedKeys={selectedKeys}
          adjustRestore={adjustRestore}
          records={records}
          readAllLog={readAllLog}
          exportAction={exportAction}
          batchDel={batchDel}
          gradeDatas={gradeDatas!}
          setLevels={setLevels}
          autoRefresh={autoRefresh}
          setAutoRefresh={setAutoRefresh}
        />
        <FieldTable
          rowKey={'id'}
          loading={loading}
          scroll={{ x: 1900 }}
          cacheKey={'device_List_table'}
          freezeHide={true}
          adjustField={hasDisplayedFieldsList}
          adjustWidth={hasDisplayedFieldsList}
          dataSource={records}
          columns={buildColumns}
          sticky={{
            getContainer: () => {
              return document.querySelector('.central-menu-content') as HTMLElement;
            },
          }}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
        />
        <Paging pagingInfo={pagingInfo} pageSizeOptions={['10', '20', '50', '100', '200']} />
      </div>
      {isVisibleModal && <AssociateLoopsModal visible={setIsVisibleModal} displayAssociate={displayAssociate!} />}

      <Modal
        title="能源资产信息"
        open={showEnergyAssetInfoVisible}
        onCancel={() => setShowEnergyAssetInfoVisible(false)}
        footer={[
          <Button key={'cancel'} onClick={() => setShowEnergyAssetInfoVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        {energyAssetInfo.filter(item => item.deviceAssetType === deviceAssetTypeEmun.PVSTATION).length !== 0 && (
          <>
            <h3 style={{ fontWeight: 700 }}>光伏能源资产编号</h3>
            <p>
              {energyAssetInfo
                .filter(item => item.deviceAssetType === deviceAssetTypeEmun.PVSTATION)
                .map(i => i.deviceAssetCode)
                .join(',')}
            </p>
          </>
        )}

        {energyAssetInfo.filter(item => item.deviceAssetType === deviceAssetTypeEmun.ENERGYSTORAGEARRAY).length !==
          0 && (
          <>
            <h3 style={{ fontWeight: 700 }}>储能能源资产编号</h3>
            <p>
              {energyAssetInfo
                .filter(item => item.deviceAssetType === deviceAssetTypeEmun.ENERGYSTORAGEARRAY)
                .map(i => i.deviceAssetCode)
                .join(',')}
            </p>
          </>
        )}

        {energyAssetInfo.filter(item => item.deviceAssetType === deviceAssetTypeEmun.NETENERGY).length !== 0 && (
          <>
            <h3 style={{ fontWeight: 700 }}>微网能源资产编号</h3>
            <p>
              {energyAssetInfo
                .filter(item => item.deviceAssetType === deviceAssetTypeEmun.NETENERGY)
                .map(i => i.deviceAssetCode)
                .join(',')}
            </p>
          </>
        )}
        {energyAssetInfo.filter(item => item.deviceAssetType === deviceAssetTypeEmun.CHARGING).length !== 0 && (
          <>
            <h3 style={{ fontWeight: 700 }}>充电站能源资产编号</h3>
            <p>
              {energyAssetInfo
                .filter(item => item.deviceAssetType === deviceAssetTypeEmun.CHARGING)
                .map(i => i.deviceAssetCode)
                .join(',')}
            </p>
          </>
        )}
        {energyAssetInfo.filter(item => item.deviceAssetType === deviceAssetTypeEmun.TOPOLOGY).length !== 0 && (
          <>
            <h3 style={{ fontWeight: 700 }}>能源介质拓扑编号</h3>
            <p>
              {energyAssetInfo
                .filter(item => item.deviceAssetType === deviceAssetTypeEmun.TOPOLOGY)
                .map(i => i.deviceAssetCode)
                .join(',')}
            </p>
          </>
        )}
        {energyAssetInfo.filter(item => item.deviceAssetType === deviceAssetTypeEmun.GAS_PREPARATION_STATION).length !==
          0 && (
          <>
            <h3 style={{ fontWeight: 700 }}>气体制备站编号</h3>
            <p>
              {energyAssetInfo
                .filter(item => item.deviceAssetType === deviceAssetTypeEmun.GAS_PREPARATION_STATION)
                .map(i => i.deviceAssetCode)
                .join(',')}
            </p>
          </>
        )}
        {energyAssetInfo.filter(item => item.deviceAssetType === deviceAssetTypeEmun.HVAC).length !== 0 && (
          <>
            <h3 style={{ fontWeight: 700 }}>暖通空调编号</h3>
            <p>
              {energyAssetInfo
                .filter(item => item.deviceAssetType === deviceAssetTypeEmun.HVAC)
                .map(i => i.deviceAssetCode)
                .join(',')}
            </p>
          </>
        )}
        {energyAssetInfo.filter(item => item.deviceAssetType === deviceAssetTypeEmun.WIND_POWER).length !== 0 && (
          <>
            <h3 style={{ fontWeight: 700 }}>风电站编号</h3>
            <p>
              {energyAssetInfo
                .filter(item => item.deviceAssetType === deviceAssetTypeEmun.WIND_POWER)
                .map(i => i.deviceAssetCode)
                .join(',')}
            </p>
          </>
        )}
      </Modal>
    </Wrapper>
  );
};

export default AlarmRecordList;
